import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { backendUrl } from '@/lib/bubbleImage';
import { Sparkle, ArrowsClockwise, Cloud, CloudRain, CloudSnow, Sun, Moon, CloudLightning, SpeakerHigh, Pause } from '@phosphor-icons/react';

const ICONS = {
  sunny: Sun, cloudy: Cloud, rainy: CloudRain, stormy: CloudLightning,
  snowy: CloudSnow, foggy: Cloud, hot: Sun, cold: CloudSnow, clear_night: Moon,
};

/**
 * BubbleAlmanac — one-line poetic whisper from the bubble based on
 * weather + time-of-day. Optionally accepts user lat/lng (asks once).
 * Auto-caches server-side for 30 min.
 */
const BubbleAlmanac = ({ bubble }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggleVoicemail = () => {
    if (!data?.voicemail_url) return;
    if (!audioRef.current) {
      const a = new Audio(backendUrl(data.voicemail_url));
      a.onended = () => setPlaying(false);
      audioRef.current = a;
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const load = async (lat, lng) => {
    setLoading(true);
    try {
      const params = {};
      if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
      const { data: d } = await api.get(`/bubbles/${bubble.id}/almanac`, { params });
      setData(d);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [bubble.id]); // eslint-disable-line

  const enableLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setCoords(c);
      load(c.lat, c.lng);
    }, () => { /* ignore */ });
  };

  const Icon = ICONS[data?.weather_kind || 'sunny'];
  return (
    <div className="glass rounded-2xl p-5" data-testid="bubble-almanac">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan flex items-center gap-1">
          <Sparkle size={11} weight="fill" /> almanac
        </div>
        <button onClick={() => load(coords?.lat, coords?.lng)}
          title="refresh"
          className="text-white/60 hover:text-white"><ArrowsClockwise size={12} /></button>
      </div>
      {loading ? (
        <div className="text-white/60 text-sm">listening for today…</div>
      ) : data ? (
        <>
          <div className="flex items-center gap-3">
            <Icon size={28} className="text-bx-cyan" weight="duotone" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/45">{data.local_time}</div>
              <div className="text-sm text-white/70 capitalize">{data.persona} · {data.mood_phrase}</div>
            </div>
          </div>
          <blockquote className="mt-4 text-base italic font-display text-white/90 border-l-2 border-bx-cyan/60 pl-3">
            "{data.whisper}"
          </blockquote>
          <div className="mt-3 flex items-center gap-3">
            {data.voicemail_url && (
              <button onClick={toggleVoicemail}
                data-testid="almanac-voicemail-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bx-cyan/15 hover:bg-bx-cyan/25 text-bx-cyan text-[11px] font-bold uppercase tracking-widest">
                {playing ? <Pause size={11} weight="fill" /> : <SpeakerHigh size={11} weight="fill" />}
                {playing ? 'pause' : `listen · ${data.voice}`}
              </button>
            )}
            {!coords && (
              <button onClick={enableLocation}
                data-testid="almanac-location-btn"
                className="text-[10px] uppercase tracking-widest text-bx-volt hover:text-white">
                let {bubble.name} feel your weather →
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="text-white/60 text-sm">the sky is quiet.</div>
      )}
    </div>
  );
};

export default BubbleAlmanac;
